import styled from "styled-components";
import { Box } from "@mui/material";
import { colors } from "../../theme";
import { media } from "../../helpers/layout";

export type Props = {
  type: "radio" | "checkbox";
  name: string;
  id: string;
  value?: string | boolean;
  label?: string;
  image?: string | React.ReactNode;
  disclaimer?: React.ReactNode;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  square?: boolean;
  imagePosition?: string;
};

const StyledInput = styled.input<Partial<Props>>`
  padding: 10px 16px;
  height: ${({ square }) => (square ? "46px" : "42px")};
  border-radius: 100px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  display: block;
  opacity: 0;
  margin: 0;
  position: absolute;
  width: 100%;

  &:checked ~ .styled-button::after {
    border-color: ${colors.black};
  }

  @media (hover: hover) {
    &:hover ~ .styled-button {
      border-color: ${colors.grey20};
    }
  }
`;

const StyledButton = styled.span<Partial<Props>>`
  position: relative;
  display: flex;
  justify-content: center;
  pointer-events: none;
  height: ${({ square }) => (square ? "46px" : "42px")};
  width: 100%;
  padding: ${({ image }) => (image ? "0" : "12px")};
  color: ${({ disabled }) => (disabled ? colors.white : colors.black)};
  background-color: ${({ disabled }) => (disabled ? colors.grey10 : colors.white)};
  border: 1px solid ${({ disabled }) => (disabled ? colors.grey02 : colors.grey10)};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.15;
  cursor: pointer;

  &::after {
    content: "";
    position: absolute;
    width: calc(100% - 2.5px);
    height: calc(100% - 2.5px);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    border: 2.5px solid transparent;
    z-index: 1;
  }

  @media ${media.m} {
    font-size: 12px;
    line-height: 1.4;
  }
`;

const SelectButton = ({
  type,
  id,
  name,
  label,
  image,
  disclaimer,
  value,
  checked,
  disabled,
  onChange,
  fullWidth,
  square,
  imagePosition,
}: Props) => {
  return (
    <Box position="relative" display="flex" width="100%" maxWidth={fullWidth ? "none" : square ? "56px" : "82px"}>
      <StyledInput
        type={type}
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        square={square}
        readOnly
      />
      <StyledButton className="styled-button" disabled={disabled} image={image} square={square}>
        {image ? (
          typeof image === "string" ? (
            <img
              src={image}
              alt={label}
              width="100%"
              height="100%"
              style={{ borderRadius: "8px", objectFit: "cover", objectPosition: square ? imagePosition : "center" }}
            />
          ) : (
            image
          )
        ) : (
          label
        )}
        {disclaimer && (
          <Box position="absolute" top="calc(100% - 8px)" width="max-content" borderRadius={1} zIndex={2}>
            {disclaimer}
          </Box>
        )}
      </StyledButton>
    </Box>
  );
};

export default SelectButton;
